import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createOrganization } from '../../api';
import Modal from '../../components/Modal';
import Alert from '../../components/Alert';
import EmptyState from '../../components/EmptyState';
import {
  PlusIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  PhotoIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

export default function OrganizationsPage() {
  const MAX_LOGO_SIZE_MB = 10;
  const MAX_LOGO_SIZE_BYTES = MAX_LOGO_SIZE_MB * 1024 * 1024;

  const { user, organizations, refreshOrgs, pinnedOrgIds, togglePinOrg } = useAuth();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ name: '', description: '', logo: '' });
  const [searchName, setSearchName] = useState('');
  const [searchOwner, setSearchOwner] = useState('');

  const isOwnerOrAdmin = user?.role === 'owner' || user?.role === 'leader' || user?.role === 'admin';
  // Non-admin users can only have max 1 org
  const canCreateOrg = isOwnerOrAdmin && (user?.role === 'admin' || organizations.length < 1);

  const filteredOrgs = organizations.filter(org => {
    const matchesName = !searchName || org.name?.toLowerCase().includes(searchName.toLowerCase());
    const matchesOwner = !searchOwner || org.ownerName?.toLowerCase().includes(searchOwner.toLowerCase());
    return matchesName && matchesOwner;
  });

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await createOrganization(form);
      await refreshOrgs();
      setShowCreate(false);
      setForm({ name: '', description: '', logo: '' });
      setSuccess('Organization created successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create organization');
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      setError(`Logo is too large. Maximum file size is ${MAX_LOGO_SIZE_MB} MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setForm(prev => ({ ...prev, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Dashboard</h2>
          <div className="subtitle">Manage your organizations</div>
        </div>
        {canCreateOrg && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <PlusIcon className="icon" /> Create Organization
          </button>
        )}
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {user?.role === 'admin' && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
            <MagnifyingGlassIcon style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: 'var(--text-muted)' }} />
            <input
              className="form-input"
              placeholder="Search by name..."
              value={searchName}
              onChange={e => setSearchName(e.target.value)}
              style={{ paddingLeft: 34 }}
            />
          </div>
          <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
            <MagnifyingGlassIcon style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: 'var(--text-muted)' }} />
            <input
              className="form-input"
              placeholder="Search by owner..."
              value={searchOwner}
              onChange={e => setSearchOwner(e.target.value)}
              style={{ paddingLeft: 34 }}
            />
          </div>
        </div>
      )}

      <div className="org-grid">
        {filteredOrgs.map(org => (
          <div key={org._id} className="org-card" onClick={() => navigate(`/org/${org._id}`)} style={{ position: 'relative' }}>
            {user?.role === 'admin' && (
              <button
                onClick={e => { e.stopPropagation(); togglePinOrg(org._id); }}
                title={pinnedOrgIds.includes(org._id) ? 'Unpin from sidebar' : 'Pin to sidebar'}
                style={{
                  position: 'absolute', top: 10, right: 10,
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: pinnedOrgIds.includes(org._id) ? 'var(--accent)' : 'var(--text-muted)',
                  padding: 4, borderRadius: 6
                }}
              >
                <MapPinIcon style={{ width: 16, height: 16, fill: pinnedOrgIds.includes(org._id) ? 'var(--accent)' : 'none' }} />
              </button>
            )}
            <div className="org-card-header">
              <div className="org-logo">
                {org.logo ? <img src={org.logo} alt="" /> : org.name?.charAt(0)?.toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="org-card-name">{org.name}</div>
                <div className="org-card-owner">Owner: {org.ownerName}</div>
              </div>
              <ArrowRightIcon style={{ width: 18, height: 18, color: 'var(--text-muted)', flexShrink: 0 }} />
            </div>
            {org.description && <div className="org-card-desc">{org.description}</div>}
            <div className="org-card-footer">
              <span><UserGroupIcon className="icon" /> {org.members?.length || 0} members</span>
            </div>
          </div>
        ))}

        {canCreateOrg && organizations.length === 0 && !searchName && !searchOwner && (
          <div className="add-org-card" onClick={() => setShowCreate(true)}>
            <div className="add-icon"><PlusIcon className="icon" /></div>
            <div style={{ fontSize: 16, fontWeight: 500 }}>Create Organization</div>
            <div style={{ fontSize: 13 }}>Set up your first organization</div>
          </div>
        )}
      </div>

      {organizations.length === 0 && !isOwnerOrAdmin && (
        <EmptyState
          icon={<BuildingOfficeIcon style={{ width: 48, height: 48 }} />}
          title="No Organizations"
          description="You haven't been added to any organizations yet. Contact your organization owner."
        />
      )}

      {/* Create Organization Modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Create Organization"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleCreateOrg}>Create</button>
          </>
        }
      >
        <form onSubmit={handleCreateOrg}>
          <div className="form-group">
            <label>Organization Name</label>
            <input className="form-input" value={form.name} onChange={e => setForm({...form, name: e.target.value.slice(0, 20)})} placeholder="e.g. Starlight Riders" maxLength={20} required />
            <div style={{ fontSize: 11, color: form.name.length >= 20 ? 'var(--danger)' : 'var(--text-muted)', marginTop: 4 }}>{form.name.length}/20 characters</div>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea className="form-input" value={form.description} onChange={e => setForm({...form, description: e.target.value.slice(0, 300)})} placeholder="Describe your organization..." rows={3} />
            <div style={{ fontSize: 11, color: form.description.length >= 300 ? 'var(--danger)' : 'var(--text-muted)', marginTop: 4 }}>{form.description.length}/300 characters</div>
          </div>
          <div className="form-group">
            <label>Logo</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {form.logo ? (
                <div className="org-logo" style={{ width: 56, height: 56 }}>
                  <img src={form.logo} alt="logo" />
                </div>
              ) : (
                <div className="org-logo" style={{ width: 56, height: 56 }}><PhotoIcon className="icon" /></div>
              )}
              <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                Upload Image
                <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
              </label>
              {form.logo && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setForm({...form, logo: ''})} style={{ color: 'var(--danger)' }}>
                  Remove
                </button>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
              Maximum file size: <strong>{MAX_LOGO_SIZE_MB} MB</strong>. If the image is too large, you will see an error message above.
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
